import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Grid, FormControl, InputLabel,
  Select, MenuItem, Switch, FormControlLabel, Alert, Stepper, Step, StepLabel,
  Divider, IconButton, ImageList, ImageListItem, ImageListItemBar, LinearProgress,
  Autocomplete,
} from '@mui/material';
import { Save, ArrowBack, ArrowForward, CloudUpload, Delete, AddPhotoAlternate, MyLocation } from '@mui/icons-material';
import { propertiesAPI, ghanaAPI } from '../../api/client';

const steps = ['Basic Info', 'Location & Pricing', 'Room Details', 'Amenities & Rules', 'Upload Photos'];

function CreatePropertyPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', property_type: 'hostel', location_type: 'off_campus',
    address: '', city: '', region: '', country: 'Ghana',
    nearby_university: '', distance_to_campus: '',
    latitude: '', longitude: '',
    price_per_month: '', price_per_semester: '', price_per_year: '', deposit_amount: '',
    currency: 'GHS', total_rooms: 1, available_rooms: 1,
    room_type: 'single', max_occupants_per_room: 1, allows_pairing: false,
    has_electricity: false, has_water: false, has_wifi: false, has_security: false,
    has_parking: false, has_laundry: false, has_kitchen: false, has_furnished: false,
    has_ac: false, has_generator: false,
    rules: '', allows_pets: false, allows_smoking: false, gender_preference: '',
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [createdPropertyId, setCreatedPropertyId] = useState(null);
  const fileInputRef = useRef(null);
  const [universities, setUniversities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);

  const fetchCities = async (region) => {
    try {
      const res = await ghanaAPI.getCities({ region });
      setCities(res.data.cities || []);
    } catch (_) {}
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [uniRes, regRes] = await Promise.all([
          ghanaAPI.getUniversities(),
          ghanaAPI.getRegions(),
        ]);
        setUniversities(uniRes.data.universities || []);
        setRegions(regRes.data.regions || []);
      } catch (_) {}
    };
    fetchData();
  }, []);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 20) {
      setError('Maximum 20 images allowed');
      return;
    }
    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    setError('');
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateProperty = async () => {
    setLoading(true);
    setError('');
    try {
      const data = { ...form };
      ['price_per_month', 'price_per_semester', 'price_per_year', 'deposit_amount', 'latitude', 'longitude'].forEach(k => {
        data[k] = data[k] ? parseFloat(data[k]) : null;
      });
      data.total_rooms = parseInt(data.total_rooms);
      data.available_rooms = parseInt(data.available_rooms);
      data.max_occupants_per_room = parseInt(data.max_occupants_per_room);
      const response = await propertiesAPI.create(data);
      setCreatedPropertyId(response.data.id);
      setActiveStep(4); // Move to upload step
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImages = async () => {
    if (!createdPropertyId || selectedFiles.length === 0) {
      navigate('/dashboard/properties');
      return;
    }
    setLoading(true);
    setError('');
    try {
      setUploadProgress(10);
      await propertiesAPI.uploadImages(createdPropertyId, selectedFiles);
      setUploadProgress(100);
      navigate('/dashboard/properties');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload images');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Property Title" required value={form.title} onChange={(e) => updateForm('title', e.target.value)} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Description" required multiline rows={4} value={form.description} onChange={(e) => updateForm('description', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select value={form.property_type} label="Property Type" onChange={(e) => updateForm('property_type', e.target.value)}>
                  <MenuItem value="hostel">Hostel</MenuItem>
                  <MenuItem value="apartment">Apartment</MenuItem>
                  <MenuItem value="shared_room">Shared Room</MenuItem>
                  <MenuItem value="single_room">Single Room</MenuItem>
                  <MenuItem value="self_contained">Self Contained</MenuItem>
                  <MenuItem value="flat">Flat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Location Type</InputLabel>
                <Select value={form.location_type} label="Location Type" onChange={(e) => updateForm('location_type', e.target.value)}>
                  <MenuItem value="on_campus">On Campus</MenuItem>
                  <MenuItem value="off_campus">Off Campus</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Address" required value={form.address} onChange={(e) => updateForm('address', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={regions} value={form.region || ''} freeSolo
                onChange={(e, v) => { updateForm('region', v || ''); updateForm('city', ''); if (v) fetchCities(v); }}
                onInputChange={(e, v) => updateForm('region', v)}
                renderInput={(params) => <TextField {...params} fullWidth label="Region" required placeholder="Search region..." />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo options={cities} value={form.city || ''}
                onInputChange={(e, v) => updateForm('city', v)}
                renderInput={(params) => <TextField {...params} fullWidth label="City / Town" required placeholder="Search city..." />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={universities.map(u => `${u.name} (${u.abbreviation})`)}
                value={form.nearby_university || ''}
                onInputChange={(e, v) => updateForm('nearby_university', v)}
                renderInput={(params) => <TextField {...params} fullWidth label="Nearby University" placeholder="Search institution..." />}
              />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Distance to Campus" value={form.distance_to_campus} onChange={(e) => updateForm('distance_to_campus', e.target.value)} placeholder="e.g. 5 mins walk" /></Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="body2" color="text.secondary">GPS Coordinates</Typography></Divider></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Latitude" type="number" value={form.latitude} onChange={(e) => updateForm('latitude', e.target.value)} placeholder="e.g. 6.5178" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Longitude" type="number" value={form.longitude} onChange={(e) => updateForm('longitude', e.target.value)} placeholder="e.g. 3.3897" /></Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button variant="outlined" startIcon={<MyLocation />} fullWidth onClick={() => {
                navigator.geolocation.getCurrentPosition(
                  (pos) => { updateForm('latitude', pos.coords.latitude.toFixed(6)); updateForm('longitude', pos.coords.longitude.toFixed(6)); },
                  () => setError('Could not get GPS location'),
                  { enableHighAccuracy: true }
                );
              }}>Get My Location</Button>
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="body2" color="text.secondary">Pricing</Typography></Divider></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Price / Month" type="number" value={form.price_per_month} onChange={(e) => updateForm('price_per_month', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Price / Semester" type="number" value={form.price_per_semester} onChange={(e) => updateForm('price_per_semester', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Price / Year" type="number" value={form.price_per_year} onChange={(e) => updateForm('price_per_year', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Deposit Amount" type="number" value={form.deposit_amount} onChange={(e) => updateForm('deposit_amount', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Currency</InputLabel>
                <Select value={form.currency} label="Currency" onChange={(e) => updateForm('currency', e.target.value)}>
                  <MenuItem value="GHS">GHS (Ghana Cedi)</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Total Rooms" type="number" value={form.total_rooms} onChange={(e) => updateForm('total_rooms', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Available Rooms" type="number" value={form.available_rooms} onChange={(e) => updateForm('available_rooms', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Room Type</InputLabel>
                <Select value={form.room_type} label="Room Type" onChange={(e) => updateForm('room_type', e.target.value)}>
                  <MenuItem value="single">Single</MenuItem>
                  <MenuItem value="double">Double</MenuItem>
                  <MenuItem value="triple">Triple</MenuItem>
                  <MenuItem value="quad">Quad</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Max Occupants per Room" type="number" value={form.max_occupants_per_room} onChange={(e) => updateForm('max_occupants_per_room', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Gender Preference</InputLabel>
                <Select value={form.gender_preference} label="Gender Preference" onChange={(e) => updateForm('gender_preference', e.target.value)}>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="male">Male Only</MenuItem>
                  <MenuItem value="female">Female Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={form.allows_pairing} onChange={(e) => updateForm('allows_pairing', e.target.checked)} />} label="Allow Room Pairing" />
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_electricity} onChange={(e) => updateForm('has_electricity', e.target.checked)} />} label="Electricity" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_water} onChange={(e) => updateForm('has_water', e.target.checked)} />} label="Water Supply" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_wifi} onChange={(e) => updateForm('has_wifi', e.target.checked)} />} label="WiFi" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_security} onChange={(e) => updateForm('has_security', e.target.checked)} />} label="Security" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_parking} onChange={(e) => updateForm('has_parking', e.target.checked)} />} label="Parking" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_laundry} onChange={(e) => updateForm('has_laundry', e.target.checked)} />} label="Laundry" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_kitchen} onChange={(e) => updateForm('has_kitchen', e.target.checked)} />} label="Kitchen" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_furnished} onChange={(e) => updateForm('has_furnished', e.target.checked)} />} label="Furnished" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_ac} onChange={(e) => updateForm('has_ac', e.target.checked)} />} label="Air Conditioning" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.has_generator} onChange={(e) => updateForm('has_generator', e.target.checked)} />} label="Generator" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.allows_pets} onChange={(e) => updateForm('allows_pets', e.target.checked)} />} label="Pets Allowed" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={form.allows_smoking} onChange={(e) => updateForm('allows_smoking', e.target.checked)} />} label="Smoking Allowed" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="House Rules" multiline rows={3} value={form.rules} onChange={(e) => updateForm('rules', e.target.value)} placeholder="Enter any house rules..." /></Grid>
          </Grid>
        );
      case 4:
        return (
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Upload photos of your property rooms, common areas, and surroundings. High-quality images attract more tenants.
            </Typography>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              variant="outlined"
              startIcon={<AddPhotoAlternate />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
            >
              Select Images (max 20)
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedFiles.length} image(s) selected
            </Typography>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />
            )}
            {previewUrls.length > 0 && (
              <ImageList cols={4} gap={8} sx={{ maxHeight: 400 }}>
                {previewUrls.map((url, index) => (
                  <ImageListItem key={index} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <img src={url} alt={`Preview ${index + 1}`} loading="lazy" style={{ height: 180, objectFit: 'cover' }} />
                    <ImageListItemBar
                      position="top"
                      actionIcon={
                        <IconButton sx={{ color: 'white' }} onClick={() => removeFile(index)}>
                          <Delete />
                        </IconButton>
                      }
                      actionPosition="right"
                      sx={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)' }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        );
      default: return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>List New Property</Typography>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {renderStep()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0 || activeStep === 4} onClick={() => setActiveStep(s => s - 1)} startIcon={<ArrowBack />}>Back</Button>
          {activeStep === 3 ? (
            <Button variant="contained" onClick={handleCreateProperty} disabled={loading} startIcon={<Save />}>
              {loading ? 'Creating...' : 'Create Property & Add Photos'}
            </Button>
          ) : activeStep === 4 ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate('/dashboard/properties')} disabled={loading}>
                Skip Photos
              </Button>
              <Button variant="contained" onClick={handleUploadImages} disabled={loading || selectedFiles.length === 0} startIcon={<CloudUpload />}>
                {loading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo(s) & Finish`}
              </Button>
            </Box>
          ) : (
            <Button variant="contained" onClick={() => setActiveStep(s => s + 1)} endIcon={<ArrowForward />}>Next</Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CreatePropertyPage;
